import { z } from "zod";

import type { Condition } from "./conditions/index.ts";
import { Operator } from "./operators.ts";

export type Obj = {
  [name: string]: ResolvableValue | PrimitiveValue;
};

export type PrimitiveValue = number | boolean | string | Obj | number[] | boolean[] | string[] | Obj[];

export type PrimitiveSingleValue = number | boolean | string | Obj;

export type PrimitiveArrayValue = number[] | boolean[] | string[] | Obj[];

export type ResolvableValue =
  | IResolvableNumberValue
  | IResolvableStringValue
  | IResolvableBooleanValue
  | IResolvableObjectValue
  | IResolvableBooleanArrayValue
  | IResolvableNumberArrayValue
  | IResolvableStringArrayValue
  | IResolvableObjectArrayValue;

export type ResolvableBooleanValue =
  | INotBooleanValue
  | IConditionBooleanValue
  | IAnyCompositeBooleanValue
  | IAllCompositeBooleanValue
  | IResolvableBooleanValue;

export type ResolvableArrayValue =
  | IResolvableBooleanArrayValue
  | IResolvableNumberArrayValue
  | IResolvableStringArrayValue
  | IResolvableObjectArrayValue;

export type ArrayValue = Array<PrimitiveValue | ResolvableValue> | ResolvableArrayValue;

export interface IValue {
  type: "number" | "string" | "boolean" | "object" | "numberArray" | "stringArray" | "booleanArray" | "objectArray";
  resolveType: string;
}

export interface ISingleValue extends IValue {
  type: "number" | "string" | "boolean" | "object";
}

export interface IResolvableNumberValue extends ISingleValue {
  type: "number";
}

export interface IResolvableStringValue extends ISingleValue {
  type: "string";
}

export interface IResolvableBooleanValue extends ISingleValue {
  type: "boolean";
}

export interface IResolvableObjectValue extends ISingleValue {
  type: "object";
}

export interface IResolvableArrayValue extends IValue {
  type: "numberArray" | "stringArray" | "booleanArray" | "objectArray";
}

export interface IResolvableBooleanArrayValue extends IResolvableArrayValue {
  type: "booleanArray";
}

export interface IResolvableNumberArrayValue extends IResolvableArrayValue {
  type: "numberArray";
}

export interface IResolvableStringArrayValue extends IResolvableArrayValue {
  type: "stringArray";
}

export interface IResolvableObjectArrayValue extends IResolvableArrayValue {
  type: "objectArray";
}

// hmmm where this is going we may need to rename "value" sometimes to "expression"
export interface INotBooleanValue extends IResolvableBooleanValue {
  resolveType: "not";
  value: ResolvableBooleanValue | boolean;
}

export interface IConditionBooleanValue extends IResolvableBooleanValue {
  resolveType: "condition";
  condition: Condition;
}

export interface IAnyCompositeBooleanValue extends IResolvableBooleanValue {
  resolveType: "any";
  values: Array<ResolvableBooleanValue | boolean>;
}

export interface IAllCompositeBooleanValue extends IResolvableBooleanValue {
  resolveType: "all";
  values: Array<ResolvableBooleanValue | boolean>;
}

export interface ICountArrayAggregateValue extends IResolvableNumberValue {
  type: "number";
  resolveType: "count";
  values: ArrayValue;
}

export interface IArrayIndexValue extends IResolvableNumberValue {
  type: "number";
  resolveType: "arrayIndex";
  values: ArrayValue;
  value: PrimitiveValue | ResolvableValue;
}

export interface IIntersectionsValue extends IResolvableNumberValue {
  type: "number";
  resolveType: "intersections";
  values: ArrayValue | ResolvableArrayValue;
  values2: ArrayValue | ResolvableArrayValue;
}

export interface IArrayConditionValue extends IResolvableBooleanValue {
  type: "boolean";
  resolveType: "arrayCondition";
  operator: Operator;
  subject: ArrayValue;
  operand: PrimitiveValue | ResolvableValue;
  operand2?: PrimitiveValue | ResolvableValue;
  composite: "all" | "any";
}

export interface IObjectPropertyValue extends ISingleValue {
  resolveType: "objectProperty";
  name: string;
  object?: IResolvableObjectValue | Obj; // supplied at runtime
}

// Zod schemas for values
// Note: Schemas use lazy evaluation to handle circular dependencies
// Schema names match interface names - TypeScript allows const and type with same name

// Base value schemas
const ValueTypeEnum = z.enum([
  "number",
  "string",
  "boolean",
  "object",
  "numberArray",
  "stringArray",
  "booleanArray",
  "objectArray",
]);
const SingleValueTypeEnum = z.enum(["number", "string", "boolean", "object"]);

const Value = z.object({
  type: ValueTypeEnum,
  resolveType: z.string(),
});

const SingleValue = Value.extend({
  type: SingleValueTypeEnum,
});

export const ResolvableNumberValue = SingleValue.extend({
  type: z.literal("number"),
});

export const ResolvableStringValue = SingleValue.extend({
  type: z.literal("string"),
});

export const ResolvableBooleanValue = SingleValue.extend({
  type: z.literal("boolean"),
});

export const ResolvableObjectValue = SingleValue.extend({
  type: z.literal("object"),
});

const ResolvableArrayValueBase = Value.extend({
  type: z.enum(["numberArray", "stringArray", "booleanArray", "objectArray"]),
});

export const ResolvableBooleanArrayValue = ResolvableArrayValueBase.extend({
  type: z.literal("booleanArray"),
});

export const ResolvableNumberArrayValue = ResolvableArrayValueBase.extend({
  type: z.literal("numberArray"),
});

export const ResolvableStringArrayValue = ResolvableArrayValueBase.extend({
  type: z.literal("stringArray"),
});

export const ResolvableObjectArrayValue = ResolvableArrayValueBase.extend({
  type: z.literal("objectArray"),
});

// Forward declarations for circular types
export const ObjZod: z.ZodType<Obj> = z.lazy(() => z.record(z.union([ResolvableValue, PrimitiveValue])));

export const PrimitiveValue: z.ZodType<PrimitiveValue> = z.lazy(() =>
  z.union([
    z.number(),
    z.boolean(),
    z.string(),
    ObjZod,
    z.array(z.number()),
    z.array(z.boolean()),
    z.array(z.string()),
    z.array(ObjZod),
  ]),
);

export const PrimitiveSingleValue: z.ZodType<PrimitiveSingleValue> = z.lazy(() =>
  z.union([z.number(), z.boolean(), z.string(), ObjZod]),
);

export const PrimitiveArrayValue: z.ZodType<PrimitiveArrayValue> = z.lazy(() =>
  z.union([z.array(z.number()), z.array(z.boolean()), z.array(z.string()), z.array(ObjZod)]),
);

// Note: ConditionBooleanValue uses z.unknown() for condition due to circular dependency
// The actual Condition type will be validated in conditions/index.ts
export const ConditionBooleanValue = z.lazy(() =>
  ResolvableBooleanValue.extend({
    resolveType: z.literal("condition"),
    condition: z
      .object({
        type: z.string(),
        operator: z.string(),
      })
      .passthrough(),
  }),
);

export const NotBooleanValue: z.ZodType<INotBooleanValue> = z.lazy(
  () =>
    ResolvableBooleanValue.extend({
      resolveType: z.literal("not"),
      value: z.union([ResolvableBooleanValueUnion, z.boolean()]),
    }) as z.ZodType<INotBooleanValue>,
);

export const AnyCompositeBooleanValue: z.ZodType<IAnyCompositeBooleanValue> = z.lazy(
  () =>
    ResolvableBooleanValue.extend({
      resolveType: z.literal("any"),
      values: z.array(z.union([ResolvableBooleanValueUnion, z.boolean()])),
    }) as z.ZodType<IAnyCompositeBooleanValue>,
);

export const AllCompositeBooleanValue: z.ZodType<IAllCompositeBooleanValue> = z.lazy(
  () =>
    ResolvableBooleanValue.extend({
      resolveType: z.literal("all"),
      values: z.array(z.union([ResolvableBooleanValueUnion, z.boolean()])),
    }) as z.ZodType<IAllCompositeBooleanValue>,
);

export const ResolvableBooleanValueUnion: z.ZodType<ResolvableBooleanValue> = z.lazy(() =>
  z.union([
    NotBooleanValue,
    ConditionBooleanValue,
    AnyCompositeBooleanValue,
    AllCompositeBooleanValue,
    ResolvableBooleanValue,
  ]),
);

export const ResolvableArrayValueUnion = z.lazy(() =>
  z.union([
    ResolvableBooleanArrayValue,
    ResolvableNumberArrayValue,
    ResolvableStringArrayValue,
    ResolvableObjectArrayValue,
  ]),
);

export const ArrayValue = z.lazy(() =>
  z.union([z.array(z.union([PrimitiveValue, ResolvableValue])), ResolvableArrayValueUnion]),
);

export const ResolvableValue = z.lazy(() =>
  z.union([
    ResolvableNumberValue,
    ResolvableStringValue,
    ResolvableBooleanValue,
    ResolvableObjectValue,
    ResolvableBooleanArrayValue,
    ResolvableNumberArrayValue,
    ResolvableStringArrayValue,
    ResolvableObjectArrayValue,
  ]),
);

export const CountArrayAggregateValue = ResolvableNumberValue.extend({
  type: z.literal("number"),
  resolveType: z.literal("count"),
  values: z.lazy(() => ArrayValue),
});

export const ArrayIndexValue = z.lazy(() =>
  ResolvableNumberValue.extend({
    type: z.literal("number"),
    resolveType: z.literal("arrayIndex"),
    values: ArrayValue,
    value: z.union([PrimitiveValue, ResolvableValue]),
  }),
);

export const IntersectionsValue = z.lazy(() =>
  ResolvableNumberValue.extend({
    type: z.literal("number"),
    resolveType: z.literal("intersections"),
    values: z.union([ArrayValue, ResolvableArrayValueUnion]),
    values2: z.union([ArrayValue, ResolvableArrayValueUnion]),
  }),
);

export const ArrayConditionValue = z.lazy(() =>
  ResolvableBooleanValue.extend({
    type: z.literal("boolean"),
    resolveType: z.literal("arrayCondition"),
    operator: z.nativeEnum(Operator),
    subject: ArrayValue,
    operand: z.union([PrimitiveValue, ResolvableValue]),
    operand2: z.union([PrimitiveValue, ResolvableValue]).optional(),
    composite: z.enum(["all", "any"]),
  }),
);

export const ObjectPropertyValue = z.lazy(() =>
  SingleValue.extend({
    resolveType: z.literal("objectProperty"),
    name: z.string(),
    object: z.union([ResolvableObjectValue, ObjZod]).optional(),
  }),
);
